// Les vacances scolaires (D-130) : la zone du club d'après son département (académies → zones A, B, C), et les dates
// officielles des vacances 2026-2027 et 2027-2028.
//
// Sources officielles : calendrier scolaire du ministère de l'Éducation nationale (education.gouv.fr) — année
// 2026-2027 ; année 2027-2028 fixée par l'arrêté du 21 juillet 2026 (Journal officiel du 23 juillet 2026). Les zones :
// zone A, académies de Besançon, Bordeaux, Clermont-Ferrand, Dijon, Grenoble, Limoges, Lyon, Poitiers ; zone B,
// Aix-Marseille, Amiens, Lille, Nancy-Metz, Nantes, Nice, Normandie, Orléans-Tours, Reims, Rennes, Strasbourg ; zone C,
// Créteil, Montpellier, Paris, Toulouse, Versailles. La Corse et l'outre-mer ont leur propre calendrier : la zone se
// choisit alors à la main. Les mêmes dates sont en base (_school_holidays, migration 20261103300000_salles_creneaux.sql) :
// src/domain/vacances.test.ts vérifie qu'elles ne divergent pas.
//
// `starts` : le premier jour des vacances (la fin des cours) ; `back` : le jour de la reprise.

export type Zone = 'A' | 'B' | 'C';
export type HolidayKey = 'toussaint' | 'noel' | 'hiver' | 'printemps' | 'ete';
export interface Holiday { season: string; key: HolidayKey; label: string; starts: string; back: string }

/** Académie de chaque département de métropole (hors Corse). */
const ACADEMIES: Record<string, string[]> = {
  'Aix-Marseille': ['04', '05', '13', '84'], Amiens: ['02', '60', '80'], 'Besançon': ['25', '39', '70', '90'],
  Bordeaux: ['24', '33', '40', '47', '64'], 'Clermont-Ferrand': ['03', '15', '43', '63'], 'Créteil': ['77', '93', '94'],
  Dijon: ['21', '58', '71', '89'], Grenoble: ['07', '26', '38', '73', '74'], Lille: ['59', '62'], Limoges: ['19', '23', '87'],
  Lyon: ['01', '42', '69'], Montpellier: ['11', '30', '34', '48', '66'], 'Nancy-Metz': ['54', '55', '57', '88'],
  Nantes: ['44', '49', '53', '72', '85'], Nice: ['06', '83'], Normandie: ['14', '27', '50', '61', '76'],
  'Orléans-Tours': ['18', '28', '36', '37', '41', '45'], Paris: ['75'], Poitiers: ['16', '17', '79', '86'],
  Reims: ['08', '10', '51', '52'], Rennes: ['22', '29', '35', '56'], Strasbourg: ['67', '68'],
  Toulouse: ['09', '12', '31', '32', '46', '65', '81', '82'], Versailles: ['78', '91', '92', '95'],
};
const ZONE_OF_ACADEMY: Record<string, Zone> = {
  'Besançon': 'A', Bordeaux: 'A', 'Clermont-Ferrand': 'A', Dijon: 'A', Grenoble: 'A', Limoges: 'A', Lyon: 'A', Poitiers: 'A',
  'Aix-Marseille': 'B', Amiens: 'B', Lille: 'B', 'Nancy-Metz': 'B', Nantes: 'B', Nice: 'B', Normandie: 'B', 'Orléans-Tours': 'B',
  Reims: 'B', Rennes: 'B', Strasbourg: 'B',
  'Créteil': 'C', Montpellier: 'C', Paris: 'C', Toulouse: 'C', Versailles: 'C',
};
export const ACADEMY_LIST = Object.keys(ACADEMIES);

/** « Toulouse » pour « 32 ». */
export const academyOf = (dep: string): string | null => Object.entries(ACADEMIES).find(([, d]) => d.includes(dep))?.[0] ?? null;
/** La zone d'un département de métropole ; null pour la Corse et l'outre-mer (calendrier à part). */
export const zoneOfDepartment = (dep: string | null | undefined): Zone | null => {
  const a = dep ? academyOf(dep.toUpperCase()) : null;
  return a ? ZONE_OF_ACADEMY[a] ?? null : null;
};

/** Le département du club : son code FFBB (« OCC0032016 » : le comité 0032, le Gers), sinon le code postal de son
 *  adresse (« 32300 Mirande »). Corse : « 2A » / « 2B » d'après le code postal (20000-20199 : 2A), « 20 » sinon. */
export function departmentOf(club: { ffbb_code?: string | null; address?: string | null }): string | null {
  const code = club.ffbb_code?.trim().toUpperCase();
  const m = code ? /^[A-Z]{3}(\d{4})\d{3}$/.exec(code) : null;
  if (m) {
    const n = Number(m[1]);
    if (n >= 971 && n <= 976) return String(n);
    if (n >= 1 && n <= 95) return String(n).padStart(2, '0');
  }
  const cp = club.address ? /\b(\d{5})\b/.exec(club.address)?.[1] : null;
  if (cp) {
    if (cp.startsWith('97')) return cp.slice(0, 3);
    if (cp.startsWith('20')) return Number(cp) < 20200 ? '2A' : '2B';
    return cp.slice(0, 2);
  }
  return null;
}

/** Les vacances officielles (zone vide : les trois zones). */
const H: [string, HolidayKey, string, string, Zone | null][] = [
  ['2026-2027', 'toussaint', '2026-10-17', '2026-11-02', null],
  ['2026-2027', 'noel', '2026-12-19', '2027-01-04', null],
  ['2026-2027', 'hiver', '2027-02-13', '2027-03-01', 'A'],
  ['2026-2027', 'hiver', '2027-02-20', '2027-03-08', 'B'],
  ['2026-2027', 'hiver', '2027-02-06', '2027-02-22', 'C'],
  ['2026-2027', 'printemps', '2027-04-10', '2027-04-26', 'A'],
  ['2026-2027', 'printemps', '2027-04-17', '2027-05-03', 'B'],
  ['2026-2027', 'printemps', '2027-04-03', '2027-04-19', 'C'],
  ['2026-2027', 'ete', '2027-07-03', '2027-09-02', null],
  ['2027-2028', 'toussaint', '2027-10-23', '2027-11-08', null],
  ['2027-2028', 'noel', '2027-12-18', '2028-01-03', null],
  ['2027-2028', 'hiver', '2028-02-19', '2028-03-06', 'A'],
  ['2027-2028', 'hiver', '2028-02-05', '2028-02-21', 'B'],
  ['2027-2028', 'hiver', '2028-02-12', '2028-02-28', 'C'],
  ['2027-2028', 'printemps', '2028-04-22', '2028-05-09', 'A'],
  ['2027-2028', 'printemps', '2028-04-08', '2028-04-24', 'B'],
  ['2027-2028', 'printemps', '2028-04-15', '2028-05-02', 'C'],
  // La rentrée 2028 n'est pas encore publiée : le 1er septembre par défaut.
  ['2027-2028', 'ete', '2028-07-05', '2028-09-01', null],
];
export const HOLIDAY_ROWS = H;
export const HOLIDAY_LABEL: Record<HolidayKey, string> = {
  toussaint: 'Vacances de la Toussaint', noel: 'Vacances de Noël', hiver: 'Vacances d’hiver', printemps: 'Vacances de printemps', ete: 'Vacances d’été',
};
/** Les puces de la liste : « Toussaint », « Noël »… */
export const HOLIDAY_CHIP: Record<HolidayKey, string> = { toussaint: 'Toussaint', noel: 'Noël', hiver: 'Hiver', printemps: 'Printemps', ete: 'Été' };
export const HOLIDAY_KEYS: HolidayKey[] = ['toussaint', 'noel', 'hiver', 'printemps', 'ete'];

export function holidays(zone: Zone, season?: string): Holiday[] {
  return H.filter(([s, , , , z]) => (!z || z === zone) && (!season || s === season))
    .map(([s, key, starts, back]) => ({ season: s, key, label: HOLIDAY_LABEL[key], starts, back }));
}
/** Les vacances qui contiennent ce jour (la reprise exclue). */
export const holidayOn = <T extends { starts: string; back: string }>(list: T[], day: string): T | null => list.find(h => day >= h.starts && day < h.back) ?? null;

const MONTHS_SHORT = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
const dayNum = (s: string) => { const d = Number(s.slice(8, 10)); return d === 1 ? '1er' : String(d); };
/** « 17 oct. – 2 nov. » (de la fin des cours à la reprise, comme le calendrier officiel). */
export const holidaySpan = (h: { starts: string; back: string }) =>
  `${dayNum(h.starts)} ${MONTHS_SHORT[Number(h.starts.slice(5, 7)) - 1]} – ${dayNum(h.back)} ${MONTHS_SHORT[Number(h.back.slice(5, 7)) - 1]}`;

// --- D-138 : les dates lues chaque saison dans les sources officielles ----------------------------------------------
// Le calendrier scolaire du ministère (jeu « fr-en-calendrier-scolaire » de data.education.gouv.fr, référencé sur
// data.gouv.fr), par ACADÉMIE (Corse et outre-mer compris), et les jours fériés (calendrier.api.gouv.fr), lus depuis
// l'appareil du bureau ou du responsable des salles (src/lib/calendrier.ts), gardés en base par club ; sans lecture, les
// dates ci-dessus (vacances) et les fériés calculés (feriesFallback) servent de secours. La zone n'est plus qu'une
// étiquette. Les mêmes listes qu'en base (_academies, _zone_of_academy, _feries_fallback : migration
// 20261104500000_salles_semaine.sql, vérifié par src/domain/vacances.test.ts).

/** Les académies que connaît le calendrier officiel : la métropole, la Corse, l'outre-mer (leur nom dans le jeu). */
export const ACADEMIES_ALL: string[] = [...ACADEMY_LIST, 'Corse', 'Guadeloupe', 'Guyane', 'La Réunion', 'Martinique', 'Mayotte', 'Nouvelle Calédonie',
  'Polynésie', 'Saint-Pierre-et-Miquelon', 'Wallis et Futuna'].sort((a, b) => a.localeCompare(b, 'fr'));
const OUTRE_MER: Record<string, string> = { '971': 'Guadeloupe', '972': 'Martinique', '973': 'Guyane', '974': 'La Réunion', '975': 'Saint-Pierre-et-Miquelon',
  '976': 'Mayotte', '986': 'Wallis et Futuna', '987': 'Polynésie', '988': 'Nouvelle Calédonie' };
/** L'académie d'un département (« 32 » → Toulouse, « 2A » → Corse, « 974 » → La Réunion). */
export function academyOfDepartment(dep: string | null | undefined): string | null {
  const d = dep?.toUpperCase() ?? null;
  if (!d) return null;
  if (d === '2A' || d === '2B' || d === '20') return 'Corse';
  return OUTRE_MER[d] ?? academyOf(d);
}
/** La zone d'une académie de métropole (une étiquette) ; la Corse et l'outre-mer n'en ont pas. */
export const zoneOfAcademy = (a: string | null | undefined): Zone | null => (a ? ZONE_OF_ACADEMY[a] ?? null : null);
/** « Gers, 32 » ; sinon « département 32 ». */
const DEP_NAMES: Record<string, string> = { '09': 'Ariège', '12': 'Aveyron', '13': 'Bouches-du-Rhône', '31': 'Haute-Garonne', '32': 'Gers', '33': 'Gironde',
  '40': 'Landes', '46': 'Lot', '47': 'Lot-et-Garonne', '57': 'Moselle', '59': 'Nord', '64': 'Pyrénées-Atlantiques', '65': 'Hautes-Pyrénées', '67': 'Bas-Rhin',
  '68': 'Haut-Rhin', '69': 'Rhône', '75': 'Paris', '81': 'Tarn', '82': 'Tarn-et-Garonne', '2A': 'Corse-du-Sud', '2B': 'Haute-Corse', '974': 'La Réunion' };
export const departmentName = (d: string) => (DEP_NAMES[d] ? `${DEP_NAMES[d]}, ${d}` : `département ${d}`);

/** La zone des jours fériés de calendrier.api.gouv.fr : l'Alsace-Moselle a les siens, l'outre-mer aussi. */
export function feriesZoneOf(dep: string | null | undefined): string {
  const d = dep?.toUpperCase() ?? '';
  if (['57', '67', '68'].includes(d)) return 'alsace-moselle';
  const dom: Record<string, string> = { '971': 'guadeloupe', '972': 'martinique', '973': 'guyane', '974': 'la-reunion', '975': 'saint-pierre-et-miquelon',
    '976': 'mayotte', '986': 'wallis-et-futuna', '987': 'polynesie-francaise', '988': 'nouvelle-caledonie' };
  return dom[d] ?? 'metropole';
}

const ymdOf = (y: number, m: number, d: number) => `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const plusDays = (s: string, n: number) => { const [y, m, d] = s.split('-').map(Number); return new Date(Date.UTC(y!, m! - 1, d! + n)).toISOString().slice(0, 10); };
/** Le dimanche de Pâques (calcul grégorien). */
export function easter(y: number): string {
  const a = y % 19, b = Math.floor(y / 100), c = y % 100, d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30, i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  return ymdOf(y, Math.floor((h + l - 7 * m + 114) / 31), ((h + l - 7 * m + 114) % 31) + 1);
}
export interface Ferie { day: string; label: string }
/** Les jours fériés de secours d'une année (les mêmes que _feries_fallback en base). */
export function feriesFallback(y: number, zone = 'metropole'): Ferie[] {
  const p = easter(y);
  const out: Ferie[] = [
    { day: ymdOf(y, 1, 1), label: '1er janvier' }, { day: plusDays(p, 1), label: 'Lundi de Pâques' }, { day: ymdOf(y, 5, 1), label: '1er mai' },
    { day: ymdOf(y, 5, 8), label: '8 mai' }, { day: plusDays(p, 39), label: 'Ascension' }, { day: plusDays(p, 50), label: 'Lundi de Pentecôte' },
    { day: ymdOf(y, 7, 14), label: '14 juillet' }, { day: ymdOf(y, 8, 15), label: 'Assomption' }, { day: ymdOf(y, 11, 1), label: 'Toussaint' },
    { day: ymdOf(y, 11, 11), label: '11 novembre' }, { day: ymdOf(y, 12, 25), label: 'Jour de Noël' },
  ];
  if (zone === 'alsace-moselle') out.push({ day: plusDays(p, -2), label: 'Vendredi saint' }, { day: ymdOf(y, 12, 26), label: '2ème jour de Noël' });
  return out.sort((a, b) => a.day.localeCompare(b.day));
}

const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[-\s]+/g, ' ').trim().toLowerCase();
/** La clé d'une période d'après la description du calendrier officiel (« Vacances de Noël », « Vacances d'Hiver »,
 *  « Vacances de Carnaval » outre-mer, « Début des Vacances d'Été »…) ; null pour le reste (pont de l'Ascension…). */
export function holidayKeyOf(description: string): HolidayKey | null {
  const s = fold(description);
  if (/pont|rentree/.test(s)) return null;
  if (/toussaint/.test(s)) return 'toussaint';
  if (/noel/.test(s)) return 'noel';
  if (/hiver|carnaval|fevrier/.test(s)) return 'hiver';
  if (/printemps|paques|avril/.test(s)) return 'printemps';
  if (/\bete\b|aout|juillet/.test(s)) return 'ete';
  return null;
}
/** La date d'une heure ISO à Paris (le jeu est donné à l'heure de Paris : « 2026-10-16T22:00:00+00:00 » → 2026-10-17). */
export function parisDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  const p = new Intl.DateTimeFormat('fr-CA', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d);
  const v = (t: string) => p.find(x => x.type === t)?.value ?? '';
  return `${v('year')}-${v('month')}-${v('day')}`;
}
export interface OfficialRecord { description?: string; population?: string; start_date?: string | null; end_date?: string | null; location?: string; annee_scolaire?: string }
/** Les vacances d'une académie d'après les enregistrements du calendrier officiel : une ligne par saison et par clé
 *  (les élèves ; un été sans fin : reprise le 1er septembre). */
export function parseCalendar(records: OfficialRecord[], academy: string, seasons: string[]): Holiday[] {
  const out = new Map<string, Holiday>();
  for (const r of records) {
    if (!r.description || !r.start_date || !r.annee_scolaire || !seasons.includes(r.annee_scolaire)) continue;
    if (r.location && fold(r.location) !== fold(academy)) continue;
    if (r.population && /enseignant/i.test(r.population)) continue;
    const key = holidayKeyOf(r.description);
    if (!key) continue;
    const starts = parisDay(r.start_date);
    const back = r.end_date ? parisDay(r.end_date) : `${starts.slice(0, 4)}-09-01`;
    if (back <= starts) continue;
    const k = `${r.annee_scolaire}:${key}`;
    const prev = out.get(k);
    // Deux lignes pour une même période (« Début des vacances d'été », « Vacances d'été ») : celle qui a sa fin.
    if (!prev || (r.end_date && back > prev.back)) out.set(k, { season: r.annee_scolaire, key, label: HOLIDAY_LABEL[key], starts, back });
  }
  return [...out.values()].sort((a, b) => a.starts.localeCompare(b.starts));
}
/** Les jours fériés de calendrier.api.gouv.fr (« { "2027-01-01": "1er janvier", … } »). */
export const parseFeries = (json: unknown): Ferie[] => (json && typeof json === 'object'
  ? Object.entries(json as Record<string, unknown>).filter(([d, l]) => /^\d{4}-\d{2}-\d{2}$/.test(d) && typeof l === 'string')
      .map(([day, label]) => ({ day, label: label as string }))
  : []).sort((a, b) => a.day.localeCompare(b.day));

/** Les adresses officielles lues (rien d'autre ne sort de l'appareil que ces requêtes publiques). */
export const CALENDAR_SOURCE = {
  vacances: (academy: string, seasons: string[]) => {
    const where = `location="${academy.replace(/"/g, '')}" and (${seasons.map(s => `annee_scolaire="${s}"`).join(' or ')})`;
    return `https://data.education.gouv.fr/api/explore/v2.1/catalog/datasets/fr-en-calendrier-scolaire/records?where=${encodeURIComponent(where)}&limit=100`;
  },
  feries: (zone: string, year: number) => `https://calendrier.api.gouv.fr/jours-feries/${zone}/${year}.json`,
};

/** Faut-il relire ? Jamais lu, une autre académie, ou pas relu depuis le dernier lundi (une lecture par semaine). */
export function calendarStale(c: { read_at: string | null; read_academy: string | null }, academy: string, now: Date): boolean {
  if (!c.read_at || c.read_academy !== academy) return true;
  const monday = new Date(now); monday.setHours(0, 0, 0, 0);
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  return new Date(c.read_at) < monday;
}
